import React, {useCallback, useContext, useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

import {FormInput} from 'src/components/input';
import {RequiredMark} from 'src/components/required-mark';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {WidthItem} from 'src/components/width-item';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {CustomValidatedControl, ValidatedControl} from 'src/contexts/validation-context';
import {ValidationContextProvider} from 'src/contexts/validation-context/validation-context-provider';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {parseAndPrettyPrintJson} from 'src/modules/entitlements/utils/parseAndPrettyPrintJson';
import {ResourceType} from 'src/openapi/admin/models/ResourceType';

import Button from '@pb/kit-component-button';
import {CheckBox} from '@pb/kit-component-checkbox';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {FormSection} from '@pb/kit-component-form-section';
import {Group, Item} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import {Textarea} from '@pb/kit-component-textarea-v2';

import {ManageEntitlementsPage} from '../components/manage-entitlements-page';
import {
    RESOURCE_DESCRIPTION_CONTROL,
    RESOURCE_EXTERNAL_ID_CONTROL,
    RESOURCE_METADATA_CONTROL,
    RESOURCE_NAME_CONTROL,
} from '../constants';
import {
    getCleanedExternalId,
    getEntitlementsReturnPath,
    isDatasetNavigationState,
    validateExternalId,
} from '../utils';
import {isValidJsonString} from '../utils/validateIsJson';

import {useCreateResource} from './useCreateResource';

import type {AccessRight} from 'src/hooks/access-rights/constants';
import type {CheckBoxChangeData} from 'src/typedef';


const CreateResource = () => {
    const navigate = useNavigate();
    const {state: navigationState} = useLocation();
    const {setErrorMessage, setSuccessMessage} = useContext(MessageContext);
    const {createResourceOpen, createResourceSubmit} = useEventTracking();
    const {proceed, setIsChunkChanged} = useConfirmationChunk({chunkName: 'create-resource', onProceed: () => {
        setSuccessMessage('Resource has been created successfully.');
        navigate(getEntitlementsReturnPath(navigationState));
    }});

    const [busy, setBusy] = useState(false);

    const {
        data,
        setData,
        saveChanges: createResourceAction,
        isChanged,
        namespaces,
    } = useCreateResource({isDataset: isDatasetNavigationState(navigationState)});

    useEffect(() => {
        setIsChunkChanged(isChanged);
    }, [isChanged, setIsChunkChanged]);

    useEffect(() => {
        if (namespaces.length) {
            setData(prev => ({
                ...prev,
                namespace: namespaces[0].code,
            }));
        }
    }, [namespaces, setData]);

    useEffect(() => {
        createResourceOpen();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const changeResourceExternalId = useCallback((id: string) => {
        setData(prev => ({...prev, id}));
    }, [setData]);

    const changeResourceName = useCallback((name: string) => {
        setData(prev => ({
            ...prev,
            name,
            id: (name && !prev.id)
                ? getCleanedExternalId(name)
                : prev.id,
        }));
    }, [setData]);

    const changeDescription = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setData(prev => ({...prev, description: event.target.value}));
    }, [setData]);

    const trimDescription = useCallback((event: React.FocusEvent<HTMLTextAreaElement>) => {
        setData(prev => ({...prev, description: event.currentTarget.value.trim()}));
    }, [setData]);

    const changeNamespace = useCallback((namespace: string) => {
        setData(prev => ({...prev, namespace}));
    }, [setData]);

    const changeMetadata = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setData(prev => ({...prev, metadata: event.target.value}));
    }, [setData]);

    const changeResourceTypes = useCallback((changeData: CheckBoxChangeData) => {
        const types = changeData.event.target.checked ? [ResourceType.DATASET] : undefined;
        setData(prev => ({...prev, types}));
    }, [setData]);

    const prettyPrintMetadata = useCallback((event: React.FocusEvent<HTMLTextAreaElement>) => {
        const value = event.currentTarget.value.trim();
        let newValue = value;

        // Only proceed if the value is not empty and is valid JSON
        if (value.length > 0 && isValidJsonString(value)) {
            try {
                const parsed = JSON.parse(value);
                // Only pretty-print if the parsed value is an object or array (not a primitive)
                if (typeof parsed === 'object' && parsed !== null) {
                    newValue = parseAndPrettyPrintJson(value);
                }
            } catch {
            }
        }
        // Set metadata to either pretty-printed JSON or the raw value
        setData(prev => ({...prev, metadata: newValue}));
    }, [setData]);

    const backToAllEntitlements = useCallback(() => {
        createResourceSubmit({submit: false, success: true});
        navigate(-1);
    }, [createResourceSubmit, navigate]);

    const createResourceHandler = useCallback(async () => {
        setBusy(true);
        const createResourceError = await createResourceAction();
        setBusy(false);

        if (createResourceError) {
            setErrorMessage(createResourceError);
        } else {
            proceed();
        }

        createResourceSubmit({submit: true, success: !createResourceError});
    }, [createResourceAction, createResourceSubmit, proceed, setErrorMessage]);

    return (
        <ServerResponseHandler
            serverError={null}
            accessRight={ACCESS_RIGHTS.MANAGE_ENTITLEMENTS as AccessRight}
            sectionName="Create Resource">
            <ManageEntitlementsPage
                title="Create Resource"
                buttonsBlock={(
                    <Group>
                        <Button variant="Type.PRIMARY" busy={busy} onClick={createResourceHandler}>
                            Create
                        </Button>
                        <Button variant="Type.SECONDARY" onClick={backToAllEntitlements}>
                            Cancel
                        </Button>
                    </Group>
                )}>
                <Item className="group-item pd-l">
                    <FormSection headline="Resource Information">
                        <Group direction="v">
                            <CustomValidatedControl name={RESOURCE_NAME_CONTROL}>
                                {error => (
                                    <FormInput
                                        value={data.name}
                                        width={300}
                                        maxLength={64}
                                        label={<RequiredMark label="Resource Name"/>}
                                        placeholder="Resource Name"
                                        onBlur={changeResourceName}
                                        valid={error ? false : undefined}
                                        errorDescription={error}
                                    />
                                )}
                            </CustomValidatedControl>
                            <CustomValidatedControl name={RESOURCE_EXTERNAL_ID_CONTROL}>
                                {error => (
                                    <FormInput
                                        value={data.id}
                                        width={300}
                                        maxLength={64}
                                        validation={validateExternalId}
                                        label={<RequiredMark label="Resource External ID"/>}
                                        placeholder="Resource External ID"
                                        onBlur={changeResourceExternalId}
                                        valid={error ? false : undefined}
                                        errorDescription={error}
                                    />
                                )}
                            </CustomValidatedControl>
                            <ValidatedControl name={RESOURCE_DESCRIPTION_CONTROL}>
                                {invalid => (
                                    <WidthItem width={610}>
                                        <Label><RequiredMark label="Description"/></Label>
                                        <Textarea
                                            maxLength={255}
                                            color={invalid ? 'ERROR' : 'DEFAULT'}
                                            value={data.description}
                                            onChange={changeDescription}
                                            onBlur={trimDescription}
                                        />
                                    </WidthItem>
                                )}
                            </ValidatedControl>
                            <ValidatedControl name={RESOURCE_METADATA_CONTROL}>
                                {invalid => (
                                    <WidthItem width={610}>
                                        <Label>Metadata</Label>
                                        <Textarea
                                            color={invalid ? 'ERROR' : 'DEFAULT'}
                                            value={data.metadata}
                                            onChange={changeMetadata}
                                            onBlur={prettyPrintMetadata}
                                        />
                                    </WidthItem>
                                )}
                            </ValidatedControl>
                            <WidthItem width={300}>
                                <Label>Namespace</Label>
                                <ComboBox
                                    value={data.namespace}
                                    onChange={changeNamespace}
                                    name="namespace">
                                    {namespaces.map(({code, description}) => (
                                        <ComboboxItem key={code} value={code} caption={description}/>
                                    ))}
                                </ComboBox>
                            </WidthItem>
                            <CheckBox
                                label="Dataset"
                                checked={Boolean(data.types?.includes(ResourceType.DATASET))}
                                onCheckBoxChange={changeResourceTypes}
                            />
                        </Group>
                    </FormSection>
                </Item>
            </ManageEntitlementsPage>
        </ServerResponseHandler>
    );
};

export const ValidateCreateResource = () => (
    <ValidationContextProvider>
        <CreateResource/>
    </ValidationContextProvider>
);
